<template>
  <el-dialog
    :model-value="visible"
    :title="isEdit ? '编辑项目' : '新建项目'"
    width="680px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="handleClose"
    class="create-project-dialog"
  >
    <el-form
      ref="formRef"
      :model="formData"
      :rules="formRules"
      label-width="120px"
      class="project-form"
    >
      <el-form-item label="项目名称" prop="projectName">
        <el-input
          v-model="formData.projectName"
          placeholder="请输入项目名称"
          maxlength="100"
          show-word-limit
          clearable
          class="custom-input"
        />
      </el-form-item>
      
      <el-form-item label="所属客户" prop="projectCustomers">
        <el-input
          v-model="formData.projectCustomers"
          placeholder="请输入所属客户"
          maxlength="100"
          show-word-limit
          clearable
          class="custom-input"
        />
      </el-form-item>
      
      <el-form-item label="案号" prop="serialNumber">
        <el-input
          v-model="formData.serialNumber"
          placeholder="请输入案号"
          maxlength="50"
          show-word-limit
          clearable
          class="custom-input"
        />
      </el-form-item>
      
      <el-form-item label="备注" prop="comment">
        <el-input
          v-model="formData.comment"
          type="textarea"
          :rows="4"
          placeholder="请输入备注信息（可选）"
          maxlength="500"
          show-word-limit
          resize="none"
          class="custom-textarea"
        />
      </el-form-item>
    </el-form>
    
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose" class="cancel-btn">取消</el-button>
        <el-button type="primary" :loading="loading" @click="handleSubmit" class="submit-btn">
          {{ isEdit ? '保存' : '创建' }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, watch, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { fetchCreate, fetchUpdate } from '@/api/project'

// 定义组件属性
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  isEdit: {
    type: Boolean,
    default: false
  },
  projectData: {
    type: Object,
    default: () => ({})
  }
})

// 定义事件
const emit = defineEmits(['update:visible', 'success', 'cancel'])

// 表单引用
const formRef = ref(null)

// 加载状态
const loading = ref(false)

// 表单数据
const formData = reactive({
  projectName: '',
  projectCustomers: '',
  serialNumber: '',
  comment: ''
})

// 表单验证规则
const formRules = {
  projectName: [
    { required: true, message: '请输入项目名称', trigger: 'blur' },
  ],
  projectCustomers: [
    { required: true, message: '请输入所属客户', trigger: 'blur' },
  ],
  serialNumber: [
    { required: true, message: '请输入案号', trigger: 'blur' },
  ],
}

// 监听弹窗显示状态，重置表单
watch(() => props.visible, (newVal) => {
  if (newVal) {
    nextTick(() => {
      resetForm()
      if (props.isEdit && props.projectData) {
        fillFormData()
      }
    })
  }
})

// 监听编辑数据变化
watch(() => props.projectData, (newVal) => {
  if (props.visible && props.isEdit && newVal) {
    fillFormData()
  }
}, { deep: true })

// 填充表单数据（编辑模式）
const fillFormData = () => {
  const { projectName, projectCustomers, serialNumber, comment } = props.projectData
  formData.projectName = projectName || ''
  formData.projectCustomers = projectCustomers || ''
  formData.serialNumber = serialNumber || ''
  formData.comment = comment || ''
}

// 重置表单
const resetForm = () => {
  formData.projectName = ''
  formData.projectCustomers = ''
  formData.serialNumber = ''
  formData.comment = ''
  
  if (formRef.value) {
    formRef.value.clearValidate()
  }
}

// 关闭弹窗
const handleClose = () => {
  emit('update:visible', false)
  emit('cancel')
}

// 提交表单
const handleSubmit = async () => {
  if (!formRef.value) return
  
  try {
    await formRef.value.validate()
    
    loading.value = true
    
    // 构建提交数据
    const submitData = {
      ...formData,
      id: props.isEdit ? props.projectData.id : undefined
    }
    
    // 模拟异步操作
    const res = await (props.isEdit ? fetchUpdate(submitData) : fetchCreate(submitData))
    if (res) {
      // 触发提交成功事件
      emit('success', res)
      ElMessage.success(props.isEdit ? '项目更新成功' : '项目创建成功')
      handleClose()
    }
    
  } catch (error) {
    console.error('表单验证失败:', error)
    loading.value = false
  }
}

// 暴露方法给父组件
defineExpose({
  resetForm,
  formData
})
</script>

<style lang="scss" scoped>
.create-project-dialog {
  :deep(.el-dialog) {
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    
    .el-dialog__header {
      padding: 24px 32px 0;
      border-bottom: 1px solid var(--el-border-color-lighter);
      margin-bottom: 0;
      background: linear-gradient(135deg, #00A05F 0%, #00C853 100%);
      
      .el-dialog__title {
        font-size: 18px;
        font-weight: 600;
        color: white;
        letter-spacing: 0.5px;
      }
      
      .el-dialog__headerbtn {
        .el-dialog__close {
          color: white;
          font-size: 18px;
          
          &:hover {
            color: rgba(255, 255, 255, 0.8);
          }
        }
      }
    }
    
    .el-dialog__body {
      padding: 32px;
      background: #f8faf9;
    }
    
    .el-dialog__footer {
      padding: 0 32px 32px;
      border-top: 1px solid var(--el-border-color-lighter);
      background: white;
    }
  }
}

.project-form {
  :deep(.el-form-item) {
    margin-bottom: 24px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .el-form-item__label {
      font-weight: 500;
      color: var(--el-text-color-primary);
      font-size: 14px;
    }
    
    .el-form-item__content {
      .custom-input,
      .custom-textarea {
        .el-input__wrapper {
          border-radius: 8px;
          border: 1px solid #e1e8ed;
          transition: all 0.3s ease;
          background: white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
          
          &:hover {
            border-color: #00A05F;
            box-shadow: 0 4px 12px rgba(0, 160, 95, 0.1);
          }
          
          &.is-focus {
            border-color: #00A05F;
            box-shadow: 0 0 0 3px rgba(0, 160, 95, 0.1);
          }
        }
        
        .el-input__inner {
          font-size: 14px;
          color: var(--el-text-color-primary);
          
          &::placeholder {
            color: #a0a0a0;
          }
        }
        
        .el-textarea__inner {
          font-size: 14px;
          color: var(--el-text-color-primary);
          border-radius: 8px;
          border: 1px solid #e1e8ed;
          transition: all 0.3s ease;
          background: white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
          
          &:hover {
            border-color: #00A05F;
            box-shadow: 0 4px 12px rgba(0, 160, 95, 0.1);
          }
          
          &:focus {
            border-color: #00A05F;
            box-shadow: 0 0 0 3px rgba(0, 160, 95, 0.1);
          }
          
          &::placeholder {
            color: #a0a0a0;
          }
        }
      }
      
      .el-input__count {
        color: #a0a0a0;
        font-size: 12px;
      }
    }
    
    &.is-error {
      .el-input__wrapper,
      .el-textarea__inner {
        border-color: var(--el-color-danger);
        box-shadow: 0 0 0 3px rgba(245, 108, 108, 0.1);
      }
    }
  }
}

.dialog-footer {
  text-align: right;
  padding-top: 24px;
  
  .cancel-btn {
    min-width: 88px;
    height: 40px;
    border-radius: 8px;
    font-weight: 500;
    border: 1px solid #e1e8ed;
    color: var(--el-text-color-regular);
    background: white;
    border-radius: 20px;
    transition: all 0.3s ease;
    
    &:hover {
      border-color: #00A05F;
      color: #00A05F;
      background: rgba(0, 160, 95, 0.05);
    }
  }
  
  .submit-btn {
    min-width: 88px;
    height: 40px;
    border-radius: 8px;
    font-weight: 500;
    margin-left: 12px;
    background: #00A05F;
    border: none;
    border-radius: 20px;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 8px 20px rgba(0, 160, 95, 0.3);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .create-project-dialog {
    :deep(.el-dialog) {
      width: 90% !important;
      margin: 5vh auto;
      
      .el-dialog__body {
        padding: 20px;
      }
      
      .el-dialog__footer {
        padding: 0 20px 20px;
      }
    }
  }
  
  .project-form {
    :deep(.el-form-item) {
      .el-form-item__label {
        width: 100px !important;
      }
    }
  }
  
  .dialog-footer {
    .cancel-btn,
    .submit-btn {
      min-width: 80px;
      height: 36px;
      font-size: 14px;
    }
  }
}

// 动画效果
.project-form {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 加载状态优化
:deep(.el-button.is-loading) {
  .el-loading-spinner {
    .circular {
      width: 16px;
      height: 16px;
    }
  }
}
</style>
